<template>
  <main>
    <LoadingOverlay :loading="isLoading" />
  </main>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { AxiosError } from "axios";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "@/apps/core-app-onboarding/composables/useI18n";

import { setLanguage } from "@/apps/core-app-onboarding/helpers/translations.helper";
import useAsset from "@/apps/core-app-onboarding/helpers/useAsset";
import logAnalyticsEvent from "@/apps/core-app-onboarding/services/google-analytics-events.service";
import { localStoragePersonUuidKey } from "@/apps/core-app-onboarding/store/core-app-onboarding.module";
import { AndroidInterface } from "@/apps/core-app-onboarding/services/android.service";
import { APIService as InStoreApiService } from "@/apps/core-app-onboarding/services/apiClient/api.service";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";

// CreditLine app services
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { RoutingService } from "@/shared-services/routing.service";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "PersonRegistrationCompleteView",
  components: { LoadingOverlay },
  beforeMount() {
    setLanguage(this);
  },
  setup() {
    /* Component state variables */
    const inStoreApi = new InStoreApiService();
    const { t } = useI18n();
    const router = useRouter();
    const routingService = new RoutingService();
    const route = useRoute();
    const isLoading = ref<boolean>(true);
    const personUuid = ref<string>("");
    const applicationId = ref<string>("");
    const storedSessionId = ref<string>(Android.getCreditLineApplicationId());
    return {
      inStoreApi,
      Android,
      isLoading,
      storedSessionId,
      router,
      route,
      routingService,
      personUuid,
      applicationId,
      useAsset,
      logAnalyticsEvent,
      t,
    };
  },
  methods: {
    hideLoader() {
      if (Android?.finishedLoading) {
        Android.finishedLoading();
      } else {
        setTimeout(() => {
          this.hideLoader();
        }, 1000);
      }
    },
    validateApplication() {
      this.isLoading = true;
      this.inStoreApi
        .validatePersonRegistration({
          applicationId: this.storedSessionId,
        })
        .then((res) => {
          this.personUuid = res?.data?.personUuid;
          const isOfferAccepted = res?.data?.isOfferSigned;
          this.applicationId = res?.data?.applicationId;

          if (!this.personUuid) {
            // ToDo: Show error screen
            return;
          }

          localStorage.setItem(localStoragePersonUuidKey, this.personUuid);
          if (isOfferAccepted) {
            Android.kycFinished(this.personUuid);
            return;
          }

          if (this.route.query.directLogin === "true") {
            this.navigateToOpenMarketOffers();
            return;
          }

          this.inStoreApi
            .checkApplicationSource({
              applicationId: this.applicationId,
            })
            .then(async (res) => {
              // Only navigate to credit pull for play store source if home_om feature flag is enabled
              if (
                res.data.applicationSource === "play store" &&
                res.data.homeOmEnabled
              ) {
                this.router.push({
                  name: "CreditPull",
                });
                return;
              }

              // Confirm if user is trully a prior borrower before proceeding to credit pull
              if (res.data.applicationSource === "prior_borrower") {
                try {
                  const res = await this.inStoreApi.checkPriorBorrower({
                    applicationId: this.applicationId,
                  });
                  if (res.data.isPriorBorrower) {
                    this.router.push({
                      name: "CreditPull",
                    });
                  } else {
                    this.router.push({
                      name: "NotAvailableView",
                    });
                  }
                  return;
                } catch (err) {
                  ErrorHandler.handleNetworkErrorAndLog(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    err as AxiosError<any, any>
                  );
                  this.router.push({
                    name: "NotAvailableView",
                  });
                }
              }

              // Kiosk goes straight to credit pull
              if (res.data.applicationSource === "kiosk") {
                this.router.push({
                  name: "CreditPull",
                });
                return;
              }

              // Internal users bypass credit pull and go straight to offers
              if (res.data.applicationSource === "internal_user") {
                this.navigateToOpenMarketOffers();
                return;
              }

              // Default to coming soon view if no specific condition is met
              this.router.push({
                name: "ComingSoonView",
              });
            });
        })
        .catch((err) => {
          ErrorHandler.handleNetworkErrorAndLog(err);
          this.isLoading = false;
        });
    },
    navigateToOpenMarketOffers() {
      const deviceInfo = JSON.parse(Android.getDeviceInfo());
      this.router.push({
        name: "OpenMarketRegistrationView",
        query: {
          source: "in_store",
          person_uuid: this.personUuid,
          applicationId: this.applicationId,
          make_model: deviceInfo.makeModel,
          manufacturer: deviceInfo.manufacturer,
        },
      });
    },
  },
  mounted() {
    // Hide Android loading overlay
    this.hideLoader();

    // Log Screen View into GA
    logAnalyticsEvent(
      AnalyticEvents.CoreAppOnboarding.OMKYCFinished,
      "CoreAppOnboarding"
    );

    this.validateApplication();
  },
});
</script>

<style lang="scss" scoped>
@import "@/apps/core-app-onboarding/styles/index.scss";
</style>
